import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import AuthModal from "../components/AuthModal";
import Footer from "../components/Footer";
import PlanCard, { DEFAULT_PLANS, mergePricing } from "../components/PlanCard";
import SpecialOffers from "../components/SpecialOffers";
import VerificationModal from "../components/VerificationModal";
import { useAuth } from "../hooks/useAuth";
import { auth } from "../firebase";

const API_BASE    = process.env.REACT_APP_PAYMENT_API_BASE || "http://localhost:5001/api";
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "";

// Features of the Janambhoomi app, taken from what the app actually offers
// (translation_provider, article_screen, search_screen, personalise_feed_screen).
const LANGUAGES = ["English", "हिन्दी", "ગુજરાતી", "मराठी"];

const APP_FEATURES = [
  {
    icon: "🌐",
    title: "4 languages",
    desc: "Read every story in English, Hindi, Gujarati or Marathi. Switch any time.",
    chips: LANGUAGES,
  },
  {
    icon: "📖",
    title: "Article reader",
    desc: "Adjustable text size, dark mode, read aloud, bookmarks and sharing.",
  },
  {
    icon: "🔍",
    title: "Search",
    desc: "Find any story by headline, topic or keyword across the full article text.",
  },
  {
    icon: "🎯",
    title: "For You feed",
    desc: "Pick your topics and editions for a personalised feed and alerts.",
  },
];

const EDITIONS = [
  { name: "Janmabhoomi", src: "/editions/janambhoomi_logo.png" },
  { name: "Vyapar Gujarati", src: "/editions/vyapar_logo.jpg" },
  { name: "Vyapar Hindi",     src: "/editions/vyapar_hindi_logo.png", wide: true },
  { name: "Kutchmitra",  src: "/editions/kutchmitra_logo.png" },
  { name: "Phulchhab",   src: "/editions/phulchhab_logo.png" },
  { name: "Pravasi",     src: "/editions/pravasi_logo.jpg" },
];

export default function SubscribePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [plans, setPlans]             = useState(DEFAULT_PLANS);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [demoMode, setDemoMode]       = useState(false);
  const [showAuth, setShowAuth]       = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  // Key of the payment in progress: "monthly" for a standard plan,
  // "student:monthly" for a verified special plan.
  const [busyKey, setBusyKey]         = useState(null);
  const [error, setError]             = useState("");
  const [special, setSpecial]         = useState(null);
  const [trial, setTrial]             = useState(null);
  // The plan the signed-in user currently has: { id, expiresAt }, or null.
  const [activePlan, setActivePlan]   = useState(null);
  const [verifications, setVerifications] = useState(null);
  const [verifyCategory, setVerifyCategory] = useState(null);
  const [notice, setNotice]           = useState("");
  const loadingPlanId = busyKey && !busyKey.includes(":") ? busyKey : null;

  // Fetch live pricing on mount (public endpoint — no auth needed)
  useEffect(() => {
    axios
      .get(`${API_BASE}/payment/pricing`)
      .then(({ data }) => {
        setPlans(mergePricing(DEFAULT_PLANS, data.pricing));
        setSpecial(data.pricing?.special || null);
        setDemoMode(!!data.demo);
        setPricingLoaded(true);
      })
      .catch(() => {
        setPricingLoaded(true);
      });
  }, []);

  // Make sure this signed-in user (email, Google or phone) has a users/{uid}
  // document, so the mobile app and the website always see the same account.
  // Failure is non-fatal: payment activation also creates the document.
  useEffect(() => {
    if (!user) return undefined;
    user.getIdToken()
      .then((t) =>
        axios.post(`${API_BASE}/payment/ensure-profile`, {}, {
          headers: { Authorization: `Bearer ${t}` },
        })
      )
      .catch(() => {});
    return undefined;
  }, [user]);

  // Trial status and Student / Senior Citizen verification status.
  const refreshAccountInfo = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) {
      setTrial(null);
      setActivePlan(null);
      setVerifications(null);
      return;
    }
    try {
      const token = await current.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [status, verification] = await Promise.all([
        axios.get(`${API_BASE}/payment/status`, { headers }).catch(() => null),
        axios.get(`${API_BASE}/payment/verification`, { headers }).catch(() => null),
      ]);
      setTrial(status?.data?.trial || null);
      setActivePlan(
        status?.data?.isPremium && status.data.plan
          ? { id: status.data.plan, expiresAt: status.data.expiresAt || null }
          : null
      );
      setVerifications(verification?.data?.verifications || null);
    } catch {
      /* non-fatal: the plans still work without this */
    }
  }, []);

  useEffect(() => {
    if (user) refreshAccountInfo();
    else { setTrial(null); setActivePlan(null); setVerifications(null); }
  }, [user, refreshAccountInfo]);

  const handleSelectPlan = useCallback(
    (plan) => {
      setError("");
      if (!user) {
        setPendingPlan(plan);
        setShowAuth(true);
        return;
      }
      startPayment(plan);
    },
    [user]
  );
  const handleAuthSuccess = useCallback(() => {
    setShowAuth(false);
    if (pendingPlan) {
      startPayment(pendingPlan);
      setPendingPlan(null);
    }
  }, [pendingPlan]);

  const startPayment = async (plan, category = null) => {
    setBusyKey(category ? `${category}:${plan.id}` : plan.id);
    setError("");
    try {
      // Use the live Firebase user, not the `user` captured by this closure:
      // right after the sign-in popup closes, that captured value is still null.
      const currentUser = auth.currentUser || user;
      if (!currentUser) throw new Error("Not signed in");
      // 1. Create order on backend (backend reads price from Firestore — client can't spoof it)
      const idToken = await currentUser.getIdToken();
      const { data } = await axios.post(
        `${API_BASE}/payment/create-order`,
        category ? { planId: plan.id, category } : { planId: plan.id },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      // 2. If demo mode (no real Razorpay keys), skip checkout and verify directly
      if (data.demo) {
        try {
          await axios.post(
            `${API_BASE}/payment/verify`,
            {
              razorpay_order_id:   data.orderId,
              razorpay_payment_id: `demo_pay_${Date.now()}`,
              razorpay_signature:  "demo_signature",
              planId:              plan.id,
            },
            { headers: { Authorization: `Bearer ${idToken}` } }
          );
          navigate("/success");
        } catch {
          setError("Demo activation failed. Is the server running?");
          setBusyKey(null);
        }
        return;
      }

      // 3. Open Razorpay checkout (real keys)
      const options = {
        key:         RAZORPAY_KEY,
        amount:      data.amount,
        currency:    data.currency || "INR",
        name:        "Janambhoomi",
        description: `JB+ ${plan.label} Membership${category ? ` (${category === "student" ? "Student" : "Senior Citizen"})` : ""}`,
        order_id:    data.orderId,
        prefill: {
          email:   currentUser.email        || "",
          name:    currentUser.displayName  || "",
          contact: currentUser.phoneNumber  || "",
        },
        theme: { color: "#0171BB" },
        handler: async (response) => {
          // 4. Verify on backend — backend writes isPremium to Firestore
          try {
            await axios.post(
              `${API_BASE}/payment/verify`,
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
                planId:              plan.id,
              },
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
            navigate("/success");
          } catch (err) {
            setError(
              err?.response?.status === 400
                ? "We could not verify this payment. If money was deducted, please contact support."
                : "Payment received but activation failed. Please contact support."
            );
            setBusyKey(null);
          }
        },
        modal: { ondismiss: () => setBusyKey(null) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setError(`Payment failed: ${resp.error.description}`);
        setBusyKey(null);
      });
      rzp.open();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not initiate payment. Please try again.");
      setBusyKey(null);
    }
  };

  const handleOfferSignIn = useCallback(() => {
    setError("");
    setShowAuth(true);
  }, []);

  const handleOfferApply = useCallback((category) => {
    setError("");
    setNotice("");
    setVerifyCategory(category);
  }, []);

  const handleOfferPay = useCallback((plan, category) => {
    setError("");
    startPayment(plan, category);
  }, []);

  return (
    <div className="page-wrap">
      <Navbar
        onLoginClick={() => setShowAuth(true)}
        onVerificationSubmitted={() => {
          setNotice("Thanks! Your document was received and is now under review. Come back here once it is approved to get your discounted price.");
          refreshAccountInfo();
        }}
      />

      {/* Hero */}
      <section className="hero">
        {demoMode && (
          <div style={{
            background: "rgba(245,200,66,0.12)", border: "1px solid rgba(245,200,66,0.4)",
            color: "#8A5A00", borderRadius: 8, padding: "10px 16px", fontSize: 13,
            fontWeight: 600, marginBottom: 20, display: "inline-flex",
            alignItems: "center", gap: 8,
          }}>
            🧪 Demo mode — no real payment will be charged. Add Razorpay test keys to go live.
          </div>
        )}
        <div className="hero-badge">✦ Premium Membership</div>
        <h1>
          Read Janambhoomi<br />
          <span className="accent">without limits</span>
        </h1>
        <p className="hero-sub">
          Subscribe to JB+ and get ad-free access to the full ePaper, exclusive
          content, and all regional editions — on web and in the app.
        </p>
        {trial?.active ? (
          <div className="trial-banner trial-banner--active">
            <strong>Your free trial is on.</strong> {trial.daysLeft} {trial.daysLeft === 1 ? "day" : "days"} left
            with every feature unlocked. You only need to choose a plan once it ends.
          </div>
        ) : (
          <div className="trial-banner">
            <strong>7 days free for every new user.</strong> Every feature is unlocked and no card
            details are needed during the trial.
          </div>
        )}
      </section>

      {/* Plans */}
      <section className="plans-section">
        {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

        {!pricingLoaded ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#666666" }}>
            <span className="spinner" style={{ borderTopColor: "#0171BB", borderColor: "rgba(1,113,187,0.2)" }} />
          </div>
        ) : (
          <div className="plans-grid">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onSelect={handleSelectPlan}
                loading={!!busyKey}
                selectedId={loadingPlanId}
                subscribed={activePlan?.id === plan.id}
                expiresAt={activePlan?.id === plan.id ? activePlan.expiresAt : null}
              />
            ))}
          </div>
        )}

        {notice && <div className="success-msg" style={{ marginTop: 20 }}>{notice}</div>}

        {pricingLoaded && special && (
          <SpecialOffers
            plans={plans}
            special={special}
            verifications={verifications}
            signedIn={!!user}
            busyKey={busyKey}
            onApply={handleOfferApply}
            onPay={handleOfferPay}
            onSignIn={handleOfferSignIn}
          />
        )}

        <p className="consent-note">
          By subscribing you agree to our <Link to="/terms">Terms of Use</Link>,{" "}
          <Link to="/privacy">Privacy Policy</Link> and{" "}
          <Link to="/refund">Refund &amp; Cancellation Policy</Link>.
        </p>
      </section>

      {/* Benefits */}
      <section className="benefits-section">
        <div className="benefits-inner">
          <div className="section-label">Why JB+</div>
          <h2 className="section-title benefits-headline">
            Ad-free reading, the full daily ePaper, exclusive JB+ stories and
            every edition, on the app and the web. One membership.
          </h2>

          <div className="editions-label">All editions included</div>
          <div className="editions-row">
            {EDITIONS.map((e) => (
              <div className={`edition-logo${e.wide ? " edition-logo--wide" : ""}`} key={e.name}>
                <img src={e.src} alt={`${e.name} logo`} loading="lazy" />
              </div>
            ))}
          </div>

          <div className="editions-label app-features-label">Also in the app</div>
          <div className="benefits-grid app-features">
            {APP_FEATURES.map((f) => (
              <div className="benefit-item" key={f.title}>
                <div className="benefit-icon" aria-hidden="true">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
                {f.chips && (
                  <div className="lang-chips">
                    {f.chips.map((c) => (
                      <span className="lang-chip" key={c}>{c}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer showRazorpay />

      {verifyCategory && (
        <VerificationModal
          category={verifyCategory}
          onClose={() => setVerifyCategory(null)}
          onSubmitted={() => {
            setVerifyCategory(null);
            setNotice("Thanks! Your document was received and is now under review. Come back here once it is approved to get your discounted price.");
            refreshAccountInfo();
          }}
        />
      )}

      {showAuth && (
        <AuthModal
          onClose={() => { setShowAuth(false); setPendingPlan(null); }}
          onSuccess={handleAuthSuccess}
        />
      )}
    </div>
  );
}
